import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Table, Tag, Typography, Space, Tooltip, Modal, message, Select, Input, Button, DatePicker, Form, Descriptions } from 'antd';
import { QuestionCircleOutlined, CopyOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import request from '../../utils/request';
import { useAuth } from '../../hooks/useAuth';
import { getProjectList, getCustomerList, getProjectDetail } from '../../services/project';
import { ChargeForm, errorText as chargeErrorText } from '../../components/billing/ChargeForm';
import { NonStdApplicableRule, NonStdChargeLinePayload, PriceHint } from '../../types/nonstandardOps';
import { getInventoryUnitDetail } from '../../services/inventory';
import { getWarehouseDetail } from '../../services/warehouse';

interface BillingRecordRow {
  id: number;
  system_code?: string;
  project_id?: number;
  customer_id?: number;
  customer_name?: string;
  operation_type?: number;
  operation_type_name?: string;
  rule_system_code?: string;
  display_name?: string;
  /** 收费依据（一句话，库存非标准操作指令终审生成的记录才有）：为什么这样收费 */
  basis_text?: string;
  unit_label?: string;
  quantity?: number | string;
  billing_unit?: string;
  unit_price?: number | string;
  amount?: number | string;
  currency?: string;
  occurred_on?: string; // 后端模型字段
  event_timestamp?: string; // 兼容另一种命名
  meta?: any; // 或 metadata
  metadata?: any;
  created_at?: string;
  project_system_code?: string;
  business_reference?: string | null;
  source_code?: string;
  source_table?: string;
  invoice?: number | null;
  invoice_system_code?: string | null;
}

/** 仓储费明细的精确金额：最多 6 位小数，去掉多余的 0，但至少保留 2 位（0.002000 → 0.002，7.620000 → 7.62） */
const formatExactAmount = (val: any): string => {
  const n = Number(val);
  if (val === undefined || val === null || val === '' || Number.isNaN(n)) return '-';
  const text = n.toFixed(6).replace(/0+$/, '');
  const [intPart, frac = ''] = text.split('.');
  return `${intPart}.${frac.padEnd(2, '0')}`;
};

// 计费价梯显示：规范类似 "31-61" 为 "31-60"（明细列和下载共用）
const ageBucketText = (v: any): string => {
  if (v === undefined || v === null || v === '') return '-';
  const s = String(v || '').trim();
  const m = s.match(/^(\d+)-(\d+)$/);
  if (m) {
    const a = parseInt(m[1], 10);
    const b2 = parseInt(m[2], 10);
    if (!Number.isNaN(a) && !Number.isNaN(b2) && b2 - a === 30) {
      return `${a}-60`;
    }
  }
  return s === '90+' ? '90+' : (s || '-');
};

const formatDecimalMax6 = (val: any): string => {
  if (val === undefined || val === null || val === '') return '-';
  const n = Number(val);
  if (Number.isNaN(n)) return '-';
  const s = n.toFixed(6).replace(/\.?0+$/, '');
  return s === '-0' ? '0' : s;
};

const BillingRecords: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BillingRecordRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 20, showSizeChanger: true });
  // 筛选条件
  const [operationTypeId, setOperationTypeId] = useState<number | undefined>(undefined);
  const [operationCategory, setOperationCategory] = useState<string | undefined>(undefined);
  const [projectId, setProjectId] = useState<number | undefined>(undefined);
  const [systemCodeExact, setSystemCodeExact] = useState<string>('');
  const [sourceCodeKeyword, setSourceCodeKeyword] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);
  const { RangePicker } = DatePicker;
  // 模拟计费日期（单日）
  const [simulateDate, setSimulateDate] = useState<string | undefined>(undefined);
  const [simulating, setSimulating] = useState(false);
  // 调试日志
  const [logs, setLogs] = useState<string[]>([]);
  const logRef = useRef<HTMLDivElement | null>(null);
  const appendLog = (msg: string, obj?: any) => {
    const ts = new Date().toISOString();
    let line = `[${ts}] ${msg}`;
    if (obj !== undefined) {
      try {
        line += ` ${JSON.stringify(obj)}`;
      } catch {}
    }
    setLogs(prev => [...prev.slice(-499), line]);
  };
  useEffect(() => {
    try {
      if (logRef.current) {
        logRef.current.scrollTop = logRef.current.scrollHeight;
      }
    } catch {}
  }, [logs]);
  // 下拉选项
  const [opTypeOptions, setOpTypeOptions] = useState<{ value: number; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: number; label: string }[]>([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [customerOptions, setCustomerOptions] = useState<{ value: number; label: string }[]>([]);
  const [nameKeyword, setNameKeyword] = useState<string>('');
  // 按收费规则系统编码模糊筛选（服务端筛选，列表和汇总、导出都按它）
  const [ruleCodeKeyword, setRuleCodeKeyword] = useState<string>('');
  const [invoiceKeyword, setInvoiceKeyword] = useState<string>('');
  const [stats, setStats] = useState<{ total_records?: number; total_amount?: number; byCurrency?: Array<{ currency: string; amount: number }> }>({});
  const [downloading, setDownloading] = useState(false);
  // 明细查看
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTitle, setDetailTitle] = useState<string>('仓储费日度明细');
  const [detailRows, setDetailRows] = useState<any[]>([]);
  const [detailPagination, setDetailPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
  });
  const [detailUnitCodes, setDetailUnitCodes] = useState<Record<string, string>>({});
  const [detailWarehouseNames, setDetailWarehouseNames] = useState<Record<string, string>>({});
  const [detailProjectInfo, setDetailProjectInfo] = useState<Record<string, { code?: string; name?: string }>>({});
  const [detailExpandedKeys, setDetailExpandedKeys] = useState<React.Key[]>([]);
  const [detailUnitFilter, setDetailUnitFilter] = useState('');
  const [detailAmount, setDetailAmount] = useState<number | undefined>(undefined);
  const [detailCurrency, setDetailCurrency] = useState<string | undefined>(undefined);
  // 正在看明细的收费记录（下载文件名和表头用）
  const [detailRecord, setDetailRecord] = useState<{ system_code?: string; occurred_on?: string } | null>(null);
  const [basisRecord, setBasisRecord] = useState<BillingRecordRow | null>(null);

  // 仓储费日度明细按“库存单元”汇总：同一个库存单元每次入库（或库存结算转入）都是一个独立的入库批次，仓储费逐批次计算，
  // 原始明细一批次一行；这里只改展示——一个库存单元一行汇总，展开才看到各批次，数据和金额都不变。
  const detailGroups = useMemo(() => {
    const groups = new Map<string, any>();
    detailRows.forEach((row: any) => {
      const key = `${row.unit_id}-${row.warehouse_id}`;
      let group = groups.get(key);
      if (!group) {
        group = { key: `unit-${key}`, isGroup: true, unit_id: row.unit_id, warehouse_id: row.warehouse_id, project_id: row.project_id, children: [] as any[] };
        groups.set(key, group);
      }
      group.children.push(row);
    });
    const list = Array.from(groups.values()).map((group: any) => {
      // 批次按入库先后（入库层编号）排序，序号从 1 开始
      const children = [...group.children].sort((x: any, y: any) => Number(x.layer_id ?? x.id) - Number(y.layer_id ?? y.id));
      const layerSeen: Record<string, any[]> = {};
      children.forEach((c: any) => { (layerSeen[String(c.layer_id ?? c.id)] = layerSeen[String(c.layer_id ?? c.id)] || []).push(c); });
      const batches = Object.keys(layerSeen).length;
      const sum = (field: string) => children.reduce((acc: number, c: any) => acc + (Number(c[field]) || 0), 0);
      const same = (field: string) => {
        const values = new Set(children.map((c: any) => String(c[field] ?? '')));
        return values.size === 1 ? children[0][field] : undefined;
      };
      return {
        ...group,
        children: children.map((c: any, index: number) => ({
          ...c,
          isGroup: false,
          batchNo: index + 1,
          // 同一个入库批次、同一天出现多行（只可能是规则版本不同的重复行）：管理员视角标出来
          duplicateVersion: layerSeen[String(c.layer_id ?? c.id)].length > 1 && c.layer_id != null,
        })),
        batches,
        qty: sum('qty'),
        total_volume_m3: sum('total_volume_m3'),
        amount: sum('amount'),
        age_bucket: same('age_bucket'),
        unit_volume_m3: same('unit_volume_m3'),
        rate: same('rate'),
      };
    });
    list.sort((x: any, y: any) => y.amount - x.amount);
    return list;
  }, [detailRows]);
  // 按库存单元系统编码筛选（不区分大小写，包含即可）
  const detailVisibleGroups = useMemo(() => {
    const keyword = detailUnitFilter.trim().toLowerCase();
    if (!keyword) return detailGroups;
    return detailGroups.filter((g: any) => String(detailUnitCodes[String(g.unit_id)] || '').toLowerCase().includes(keyword));
  }, [detailGroups, detailUnitFilter, detailUnitCodes]);
  const detailBatchCount = useMemo(() => detailVisibleGroups.reduce((acc: number, g: any) => acc + g.children.length, 0), [detailVisibleGroups]);

  useEffect(() => {
    setDetailPagination((prev) => ({
      ...prev,
      current: 1,
      total: detailVisibleGroups.length,
    }));
  }, [detailVisibleGroups]);
  // 管理员：手动创建收费记录
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualProjectLoading, setManualProjectLoading] = useState(false);
  // 先选客户（可再选项目）才能选规则：选了项目 = 系统级 + 客户级 + 项目级，只选客户 = 系统级 + 客户级；null 表示还在加载
  const [manualRules, setManualRules] = useState<NonStdApplicableRule[] | null>(null);
  const [manualProjectOptions, setManualProjectOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [manualForm] = Form.useForm();
  // 批量下载按钮数量显示：若按系统编码精确筛选，则优先使用列表接口返回的 count（total）；否则用后端统计总数
  const exportCount = useMemo(() => {
    const hasSys = (systemCodeExact || '').trim().length > 0;
    return hasSys ? (total || 0) : (stats.total_records || 0);
  }, [systemCodeExact, total, stats.total_records]);
  // 前端手工合计（基于当前表格可见数据 data）
  const frontByCurrency = useMemo(() => {
    const map = new Map<string, number>();
    (data || []).forEach((r) => {
      const cur = (r.currency || 'UNK') as string;
      const v = Number(r.amount || 0);
      if (!Number.isNaN(v)) map.set(cur, (map.get(cur) || 0) + v);
    });
    return Array.from(map.entries()).map(([currency, amount]) => ({ currency, amount }));
  }, [data]);

  // 基于相关业务单号（source_code）分组并交替背景色（仅当前页数据）
  const groupIndexBySource = useMemo(() => {
    const map = new Map<string, number>();
    let toggle = 0;
    (data || []).forEach((r) => {
      const key = (r.source_code || '').trim();
      if (!key) return; // 无相关业务单号不参与分组着色
      if (!map.has(key)) {
        map.set(key, toggle % 2);
        toggle += 1;
      }
    });
    return map;
  }, [data]);

  const columns: ColumnsType<BillingRecordRow> = useMemo(() => [
    {
      title: '收费记录系统编码',
      dataIndex: 'system_code',
      key: 'system_code',
      render: (v?: string) => {
        if (!v) return '-';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{v}</span>
            <Tooltip title="复制">
              <CopyOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(v);
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },
    },
    { title: '项目编码', dataIndex: 'project_system_code', key: 'project_system_code', render: (v?: string) => v || '-' },
    { title: '操作类型', dataIndex: 'operation_type_name', key: 'operation_type_name', render: (_: any, r) => r.operation_type_name || r.operation_type || '-' },
    { title: (
        <span>
          收费规则系统编码
          <Tooltip title="悬停查看规则说明">
            <QuestionCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'rule_system_code', key: 'rule_system_code', render: (v: string | undefined, r: BillingRecordRow) => {
        const text = v || '-';
        const desc = (r as any)?.description || undefined;
        if (!v) return text;
        const code = v;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {desc ? (
              <Tooltip title={desc} placement="top">
                <span style={{ color: '#1677ff' }}>{text}</span>
              </Tooltip>
            ) : <span>{text}</span>}
            <Tooltip title="复制">
              <CopyOutlined
                className="rule-code-copy"
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(code);
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      }
    },
    { title: '收费项目名称', dataIndex: 'display_name', key: 'display_name', render: (v: string | undefined, r: BillingRecordRow) => v || r.operation_type_name || '-' },
    { title: '收费单位', dataIndex: 'unit_label', key: 'unit_label', render: (v, r) => v || r.billing_unit || '-' },
    { title: '单价', dataIndex: 'unit_price', key: 'unit_price', align: 'right', render: (v) => (v !== undefined && v !== null ? Number(v).toFixed(2) : '-') },
    { title: '计费数量', dataIndex: 'quantity', key: 'quantity', align: 'right', render: (v) => formatDecimalMax6(v) },
    { title: '金额', dataIndex: 'amount', key: 'amount', align: 'center', render: (v, r) => {
      if (v === undefined || v === null) return '-';
      const text = Number(v).toFixed(2);
      const key = (r?.source_code || '').trim();
      const idx = key ? groupIndexBySource.get(key) : undefined;
      const bg = idx === 0 ? '#e6f7ff' : (idx === 1 ? '#fff7e6' : '#ffffff');
      return (
        <span
          style={{
            background: bg,
            border: '1px solid #91d5ff',
            borderRadius: 6,
            display: 'inline-block',
            minWidth: 72,
            textAlign: 'center'
          }}
        >
          {text}
        </span>
      );
    } },
    { title: '货币', dataIndex: 'currency', key: 'currency' },
    { title: '相关业务单号', dataIndex: 'source_code', key: 'source_code', render: (v?: string) => {
      if (!v) return '-';
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span>{v}</span>
          <Tooltip title="复制">
            <CopyOutlined
              style={{ color: '#1677ff', cursor: 'pointer' }}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await navigator.clipboard.writeText(v);
                  message.success('已复制');
                } catch {
                  message.error('复制失败');
                }
              }}
            />
          </Tooltip>
        </span>
      );
    } },
    { title: '发生日期', dataIndex: 'occurred_on', key: 'occurred_on', render: (_: any, r) => r.occurred_on || r.event_timestamp || '-' },
    {
      title: '归属账单',
      dataIndex: 'invoice_system_code',
      key: 'invoice_system_code',
      render: (_: any, r) => {
        const code = r.invoice_system_code || (r.invoice ? `Invoice-${r.invoice}` : '');
        if (!code) return '-';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{code}</span>
            <Tooltip title="复制">
              <CopyOutlined
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(code);
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },
    },
    { title: '操作', key: 'actions', render: (_: any, r) => {
      const meta: any = (r as any).meta || (r as any).metadata || {};
      const ids = Array.isArray(meta.daily_charge_ids) ? meta.daily_charge_ids : [];
      // 库存非标准操作指令终审生成的记录（meta.strategy === 'nonstandard'）的名称是管理员写的收费项目说明，不能按名称含“仓储费”判断成仓储费记录
      const isNonStd = meta?.strategy === 'nonstandard';
      const isStorage = (r as any).source_table === 'billing.StorageDailyCharge' || ids.length > 0 || (!isNonStd && /仓储费/.test(String(r.display_name || r.operation_type_name || '')));
      const isManual = (r as any).source_table === 'billing.ManualEntry' || meta?.manual_entry === true;
      // 收费依据（库存非标准操作指令生成的、管理员手动创建的记录才有）和手动记录的内部备注，合在一个弹窗里看
      const hasBasis = !!String((r as any).basis_text || '').trim() || (isManual && !!String(meta?.note || meta?.remark || '').trim());
      if (!isStorage && !isManual && !hasBasis) return null;
      return (
        <Space>
          {hasBasis && (
            <Button size="small" type="link" onClick={(e) => { e.stopPropagation(); setBasisRecord(r); }}>查看依据</Button>
          )}
          {isStorage && (
            <Button size="small" type="link" disabled={!isStorage} onClick={async (e) => {
              e.stopPropagation();
              try {
                if (!isStorage) { message.info('非仓储费记录'); return; }
                if (!r.occurred_on) { message.warning('缺少发生日期'); return; }
                setDetailTitle(`仓储费日度明细 - ${r.occurred_on}`);
                // 保存选中记录的金额与币种，用于标题右侧展示
                const amtNum = Number((r as any).amount);
                setDetailAmount(Number.isNaN(amtNum) ? undefined : amtNum);
                setDetailCurrency((r as any).currency);
                setDetailRecord({ system_code: r.system_code, occurred_on: r.occurred_on });
                setDetailExpandedKeys([]);
                setDetailUnitFilter('');
                setDetailOpen(true);
                setDetailLoading(true);
                const date = r.occurred_on as string;
                const pid = r.project_id as number | undefined;
                const params: any = { charge_date: date };
                if (pid) params.project_id = pid;
                appendLog('[detail] daily-charges request', params);
                const list: any[] = [];
                let page = 1;
                const pageSize = 500;
                while (true) {
                  const pageParams = { ...params, page, page_size: pageSize };
                  const res = await request.get('/api/v1/billing/storage/daily-charges/', { params: pageParams });
                  const pageList = Array.isArray(res?.data?.results) ? res.data.results : [];
                  list.push(...pageList);
                  const totalCount = Number(res?.data?.count || 0);
                  if (list.length >= totalCount || pageList.length === 0) break;
                  page += 1;
                }
                let finalRows = list;
                if (ids.length > 0) {
                  const idSet = new Set(ids.map((x: any) => String(x)));
                  finalRows = list.filter((it: any) => idSet.has(String(it.id)));
                }
                // 排序：按金额 desc
                finalRows.sort((a: any, b: any) => Number(b.amount || 0) - Number(a.amount || 0));
                setDetailRows(finalRows);
                // 加载系统编码映射/仓库名称/项目信息
                try {
                  const ids = Array.from(new Set(finalRows.map((it: any) => it.unit_id).filter((x: any) => x != null)));
                  const codeMap: Record<string, string> = {};
                  await Promise.all(ids.map(async (uid: any) => {
                    try {
                      const resp = await getInventoryUnitDetail(uid);
                      const d: any = (resp as any)?.data || resp;
                      const code = d?.system_barcode || d?.system_code || '';
                      if (code) codeMap[String(uid)] = String(code);
                    } catch {}
                  }));
                  setDetailUnitCodes(codeMap);
                  // 仓库
                  const wids = Array.from(new Set(finalRows.map((it: any) => it.warehouse_id).filter((x: any) => x != null)));
                  const wMap: Record<string, string> = {};
                  await Promise.all(wids.map(async (wid: any) => {
                    try {
                      const resp = await getWarehouseDetail(Number(wid));
                      const d: any = (resp as any)?.data || resp;
                      const name = d?.name || d?.warehouse_name || '';
                      if (name) wMap[String(wid)] = String(name);
                    } catch {}
                  }));
                  setDetailWarehouseNames(wMap);
                  // 项目
                  const pids = Array.from(new Set(finalRows.map((it: any) => it.project_id).filter((x: any) => x != null)));
                  const pMap: Record<string, { code?: string; name?: string }> = {};
                  await Promise.all(pids.map(async (pid: any) => {
                    try {
                      const resp = await getProjectDetail(Number(pid));
                      const d: any = (resp as any)?.data || resp;
                      pMap[String(pid)] = { code: d?.project_code, name: d?.name };
                    } catch {}
                  }));
                  setDetailProjectInfo(pMap);
                } catch {}
                appendLog('[detail] daily-charges response', { count: finalRows.length, sample: finalRows.slice(0, 5) });
              } catch (err) {
                appendLog('[detail] error', err);
                message.error('加载明细失败');
              } finally {
                setDetailLoading(false);
              }
            }}>查看明细</Button>
          )}
        </Space>
      );
    } }
  ], [groupIndexBySource]);

  // 判断是否管理员后台：尽量兼容多种标记
  const isAdmin = useMemo(() => {
    const u: any = user || {};
    return !!(u?.is_staff || u?.is_superuser || u?.role === 'admin' || u?.roles?.includes?.('admin') || u?.permissions?.includes?.('admin'));
  }, [user]);

  // 仓储费日度明细下载（Excel 两张表）：“按库存单元汇总”一个库存单元一行，“入库批次明细”一个入库批次一行（每行带库存单元编码，便于筛选和透视）。
  // 跟随弹窗里的库存单元筛选；不受分页影响。数量、体积、金额按数字写入，金额保留 6 位小数（和明细一致，不先取整）。
  const [detailDownloading, setDetailDownloading] = useState(false);
  const downloadDetailExcel = async () => {
    if (detailVisibleGroups.length === 0) return;
    setDetailDownloading(true);
    try {
      const XLSX = await import('xlsx');
      const keyword = detailUnitFilter.trim();
      const num6 = (v: any) => (v === undefined || v === null || v === '' || Number.isNaN(Number(v)) ? '' : Number(Number(v).toFixed(6)));
      const unitCode = (g: any) => detailUnitCodes[String(g.unit_id)] || String(g.unit_id ?? '');
      const warehouseText = (id: any) => detailWarehouseNames[String(id)] || String(id ?? '');
      const projectText = (id: any) => {
        const info = detailProjectInfo[String(id)] || {};
        const code = info.code || String(id ?? '');
        return `${code}${info.name ? ` - ${info.name}` : ''}`;
      };
      const currency = detailCurrency || 'USD';
      const exactTotal = detailGroups.reduce((acc: number, g: any) => acc + Number(g.amount || 0), 0);
      const groups = detailVisibleGroups;
      const total = (field: string) => groups.reduce((acc: number, g: any) => acc + (Number(g[field]) || 0), 0);

      const summary: any[][] = [
        ['仓储费日度明细'],
        ['收费记录编码', detailRecord?.system_code || '-'],
        ['计费日期', detailRecord?.occurred_on || '-'],
        ['币种', currency],
        ['明细精确合计', num6(exactTotal)],
        ['本条收费记录入账金额（按分四舍五入）', detailAmount != null ? Number(detailAmount.toFixed(2)) : ''],
        ['说明', '同一个库存单元每次入库、每次库存结算转入，都会形成一个独立的入库批次，仓储费按批次分别计算；各入库批次见“入库批次明细”工作表。'],
        ...(keyword ? [['筛选', `按库存单元系统编码“${keyword}”筛选后的明细（明细精确合计和入账金额仍是整条收费记录的）`]] : []),
        [],
        ['库存单元系统编码', '仓库', '项目', '入库批次数', '数量合计', '总体积合计(m³)', `当日金额(${currency})`],
        ...groups.map((g: any) => [unitCode(g), warehouseText(g.warehouse_id), projectText(g.project_id), g.batches, Number(g.qty) || 0, num6(g.total_volume_m3), num6(g.amount)]),
        ['合计', '', '', total('batches'), total('qty'), num6(total('total_volume_m3')), num6(total('amount'))],
      ];
      const batchHead = ['库存单元系统编码', '入库批次', '入库日期', '仓库', '项目', '计费价梯(天)', '数量', '单位体积(m³)', '总体积(m³)', '费率(每m³/日)', `金额(${currency})`];
      const batches: any[][] = [isAdmin ? [...batchHead, '入库层编号', '规则版本重复'] : batchHead];
      groups.forEach((g: any) => {
        (g.children || []).forEach((c: any) => {
          const row: any[] = [
            unitCode(g), c.batchNo, c.layer_received_on || '', warehouseText(g.warehouse_id), projectText(g.project_id), ageBucketText(c.age_bucket),
            Number(c.qty) || 0, num6(c.unit_volume_m3), num6(c.total_volume_m3), num6(c.rate), num6(c.amount),
          ];
          if (isAdmin) row.push(c.layer_id ?? '', c.duplicateVersion ? `是（规则版本 ${c.rule_version}）` : '');
          batches.push(row);
        });
      });

      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.aoa_to_sheet(summary);
      ws1['!cols'] = [{ wch: 36 }, { wch: 18 }, { wch: 28 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, ws1, '按库存单元汇总');
      const ws2 = XLSX.utils.aoa_to_sheet(batches);
      ws2['!cols'] = [{ wch: 24 }, { wch: 10 }, { wch: 12 }, { wch: 18 }, { wch: 28 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws2, '入库批次明细');
      XLSX.writeFile(wb, `仓储费明细_${detailRecord?.system_code || 'record'}_${detailRecord?.occurred_on || ''}.xlsx`);
    } catch (e: any) {
      message.error(`下载失败：${e?.message || '未知错误'}`);
    } finally {
      setDetailDownloading(false);
    }
  };
  const manualCustomerId = Form.useWatch('customer_id', manualForm);
  const manualProjectId = Form.useWatch('project_id', manualForm);

  const loadManualProjectsByCustomer = async (cid?: number) => {
    if (!cid) {
      setManualProjectOptions([]);
      return;
    }
    setManualProjectLoading(true);
    try {
      const resp = await getProjectList({ page: 1, page_size: 500, customer: cid });
      const list = Array.isArray((resp.data as any)?.results) ? (resp.data as any).results : [];
      setManualProjectOptions(
        list.map((p: any) => ({ value: Number(p.id), label: `${p.project_code || p.id} - ${p.name || ''}` }))
      );
    } catch {
      setManualProjectOptions([]);
    } finally {
      setManualProjectLoading(false);
    }
  };

  const openManualCreateModal = () => {
    manualForm.resetFields();
    setManualProjectOptions([]);
    setManualRules(null);
    setManualOpen(true);
  };

  // 选了客户（可再选项目）后，取这个范围内适用的收费规则
  useEffect(() => {
    if (!manualOpen || !manualCustomerId) { setManualRules(null); return undefined; }
    let alive = true;
    setManualRules(null);
    request.get('/api/v1/billing/billing-records/applicable-rules/', {
      params: { customer_id: manualCustomerId, ...(manualProjectId ? { project_id: manualProjectId } : {}) },
    })
      .then((resp: any) => { if (alive) setManualRules(Array.isArray(resp?.data?.rules) ? resp.data.rules : []); })
      .catch((e: any) => { if (alive) { setManualRules([]); message.error(chargeErrorText(e, '加载收费规则失败，可以先用“临时收费”')); } });
    return () => { alive = false; };
  }, [manualOpen, manualCustomerId, manualProjectId]);

  const fetchManualHint = useCallback(async (params: { rule_id: number; quantity: number; tier_value?: number }): Promise<PriceHint> => {
    const resp: any = await request.get('/api/v1/billing/billing-records/price-hint/', {
      params: { customer_id: manualCustomerId, ...(manualProjectId ? { project_id: manualProjectId } : {}), ...params },
    });
    return resp.data;
  }, [manualCustomerId, manualProjectId]);

  // 客户、项目、发生日期、内部备注在弹窗上方的表单里，规则/单价/数量在下面的收费表单里；收费表单点“创建收费记录”时一起提交
  const handleManualCreate = async (charge: NonStdChargeLinePayload) => {
    let values: any;
    try {
      values = await manualForm.validateFields();
    } catch {
      message.warning('请先把上面的客户、发生日期和内部备注填完整');
      return;
    }
    setManualSubmitting(true);
    try {
      await request.post('/api/v1/billing/billing-records/manual-create/', {
        ...charge,
        customer_id: values.customer_id,
        project_id: values.project_id || undefined,
        occurred_on: values.occurred_on?.format?.('YYYY-MM-DD'),
        note: values.note,
        // 相关业务单号（选填）：不填后端用自动编号 MANUAL-…
        source_code: String(values.source_code || '').trim() || undefined,
      });
      message.success('手动收费记录创建成功');
      setManualOpen(false);
      fetchData((pagination.current as number) || 1, (pagination.pageSize as number) || 20);
    } catch (e: any) {
      throw e; // 收费表单会把后端返回的原因（缺哪个字段、规则不适用等）显示出来
    } finally {
      setManualSubmitting(false);
    }
  };

  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        page_size: pageSize,
        ordering: '-created_at',
        locale: 'zh-CN',
      };
      if (systemCodeExact) params.system_code = systemCodeExact;
      if (dateRange?.[0]) params.occurred_from = dateRange[0];
      if (dateRange?.[1]) params.occurred_to = dateRange[1];
      if (operationTypeId) params.operation_type = operationTypeId;
      if (operationCategory) params.operation_category = operationCategory;
      if (projectId) params.project_id = projectId;
      if (isAdmin && customerId) params.customer_id = customerId;
      if (sourceCodeKeyword) params.source_code = sourceCodeKeyword;
      if (nameKeyword) params.display_name = nameKeyword;
      if (ruleCodeKeyword) params.rule_system_code = ruleCodeKeyword;
      if (invoiceKeyword) params.invoice_ref = invoiceKeyword;

      // DEBUG: 打印请求参数
      // eslint-disable-next-line no-console
      console.debug('[BillingRecords] fetch params:', params);
      appendLog('[fetch] params', params);

      const [res, statsRes] = await Promise.all([
        request.get('/api/v1/billing/billing-records/', { params }),
        // 统计不需要分页参数
        request.get('/api/v1/billing/billing-records/statistics/', { params: {
          system_code: systemCodeExact || undefined,
          occurred_from: params.occurred_from,
          occurred_to: params.occurred_to,
          operation_type: params.operation_type,
          operation_category: params.operation_category,
          project_id: params.project_id,
          customer_id: isAdmin ? (customerId || undefined) : undefined,
          source_code: sourceCodeKeyword || undefined,
          display_name: nameKeyword || undefined,
          rule_system_code: ruleCodeKeyword || undefined,
          invoice_ref: invoiceKeyword || undefined,
        } })
      ]);

      const list = (res.data?.results || []) as BillingRecordRow[];
      // 列表侧仍保留本地关键词过滤（当前页），与后端统计可能存在口径差异
      const kw1 = sourceCodeKeyword.trim().toLowerCase();
      const kw2 = nameKeyword.trim().toLowerCase();
      const kwRule = ruleCodeKeyword.trim().toLowerCase();
      const sysExact = systemCodeExact.trim().toLowerCase();
      const filtered = list.filter(r => {
        const byRuleCode = kwRule ? String(r.rule_system_code || '').toLowerCase().includes(kwRule) : true;
        if (!byRuleCode) return false;
        const bySys = sysExact ? String(r.system_code || '').toLowerCase() === sysExact : true;
        const bySource = kw1 ? (r.source_code || '').toLowerCase().includes(kw1) : true;
        const disp = (r.display_name || r.operation_type_name || '').toString().toLowerCase();
        const byName = kw2 ? disp.includes(kw2) : true;
        return bySys && bySource && byName;
      });
      setData(filtered);
      try {
        const hasRule = filtered.filter((r: any) => !!(r as any).rule_system_code).length;
        const storageRows = filtered.filter((r: any) => (r as any).source_table === 'billing.StorageDailyCharge');
        const storageHasRule = storageRows.filter((r: any) => !!(r as any).rule_system_code).length;
        appendLog('[rule-trace] fetched page summary', {
          total_in_page: filtered.length,
          with_rule_system_code: hasRule,
          storage_rows: storageRows.length,
          storage_with_rule_system_code: storageHasRule,
        });
        const sample = filtered.slice(0, 5).map((r: any) => ({
          id: r.id,
          source_table: r.source_table,
          rule_system_code: (r as any).rule_system_code,
          meta_rule_system_code: (r as any)?.meta?.rule_system_code,
          meta_rule_set_id: (r as any)?.meta?.rule_set_id,
        }));
        appendLog('[rule-trace] sample', sample);
      } catch {}
      // 当按“收费记录系统编码”精确筛选时，分页总数以前端精确过滤后的条数为准，避免后端未实现该过滤导致总数不符
      setTotal(sysExact ? filtered.length : (res.data?.count || 0));
      appendLog('[fetch] result count', { page, pageSize, count: res.data?.count, filtered: filtered.length });
      // 回填操作类型选项（当接口返回空时，使用当前页数据构建）
      if ((!opTypeOptions || opTypeOptions.length === 0) && Array.isArray(list) && list.length > 0) {
        const map = new Map<number, string>();
        list.forEach(r => {
          const id = Number(r.operation_type as any);
          const name = r.operation_type_name || String(r.operation_type || '');
          if (!Number.isNaN(id) && name) map.set(id, name);
        });
        if (map.size > 0) {
          setOpTypeOptions(Array.from(map.entries()).map(([value, label]) => ({ value, label })));
        }
      }

      // 取消兜底：客户选项仅来源于“全量客户”接口

      // 统计结果处理
      try {
        const s = statsRes.data || {};
        const byCurrency: Array<{ currency: string; amount: number }> = [];
        const rc = s.records_by_currency || {};
        Object.keys(rc).forEach(cur => {
          byCurrency.push({ currency: cur, amount: Number(rc[cur] || 0) });
        });
        setStats({ total_records: s.total_records, total_amount: s.total_amount, byCurrency });
        // DEBUG: 打印统计响应
        // eslint-disable-next-line no-console
        console.debug('[BillingRecords] stats response:', s);
        appendLog('[stats] response', s);
      } catch {
        setStats({});
      }
    } catch (e) {
      // 已有全局拦截器处理
      appendLog('[fetch] error', e);
    } finally {
      setLoading(false);
    }
  };

  // 轮询近况：查询 StorageDailyCharge 是否写入（用于开发态校验任务完成）
  const pollDailyCharges = async (date: string, project?: number, tries = 10, intervalMs = 1500) => {
    for (let i = 0; i < tries; i += 1) {
      try {
        const params: any = { charge_date: date };
        if (project) params.project_id = project;
        const res = await request.get('/api/v1/billing/storage/daily-charges/', { params });
        const count = Number(res?.data?.count || 0);
        if (count > 0) return true;
      } catch {}
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    return false;
  };

  // 手动检查：当日日度明细
  const [checkingDaily, setCheckingDaily] = useState(false);
  const handleCheckDailyCharges = async () => {
    const date = simulateDate || dateRange?.[0];
    if (!date) {
      message.warning('请先选择“模拟计费日期”或设置“发生日期”开始值');
      return;
    }
    try {
      setCheckingDaily(true);
      const params: any = { charge_date: date };
      if (projectId) params.project_id = projectId;
      appendLog('[check] daily-charges request', params);
      const res = await request.get('/api/v1/billing/storage/daily-charges/', { params });
      const count = Number(res?.data?.count || 0);
      const items = Array.isArray(res?.data?.results) ? res.data.results.slice(0, 5) : [];
      appendLog('[check] daily-charges response', { count, sample: items });
      message.info(`当日日度明细：${count} 条`);
    } catch (e) {
      appendLog('[check] daily-charges error', e);
      message.error('查询日度明细失败');
    } finally {
      setCheckingDaily(false);
    }
  };

  // 手动检查：当日异常
  const [checkingAnom, setCheckingAnom] = useState(false);
  const handleCheckAnomalies = async () => {
    const date = simulateDate || dateRange?.[0];
    if (!date) {
      message.warning('请先选择“模拟计费日期”或设置“发生日期”开始值');
      return;
    }
    try {
      setCheckingAnom(true);
      const params: any = { charge_date: date };
      if (projectId) params.project_id = projectId;
      appendLog('[check] anomalies request', params);
      const res = await request.get('/api/v1/billing/storage/anomalies/', { params });
      const count = Number(res?.data?.count || 0);
      const items = Array.isArray(res?.data?.results) ? res.data.results.slice(0, 5) : [];
      appendLog('[check] anomalies response', { count, sample: items });
      if (count > 0) {
        message.warning(`当日异常：${count} 条（已写入日志区块 sample）`);
      } else {
        message.success('当日异常：0 条');
      }
    } catch (e) {
      appendLog('[check] anomalies error', e);
      message.error('查询异常失败');
    } finally {
      setCheckingAnom(false);
    }
  };

  // 触发重算：以模拟计费日期生成当日仓储费（日度明细落库）
  const handleSimulateGenerate = async () => {
    if (!simulateDate) {
      message.warning('请选择模拟计费日期');
      return;
    }
    try {
      setSimulating(true);
      const body: any = { start_date: simulateDate, end_date: simulateDate };
      if (projectId) body.project_id = projectId;
      appendLog('[simulate] recalc request', body);
      const resp = await request.post('/api/v1/billing/storage/daily-charges/recalc/', body);
      // 可选：展示触发数量
      const enq = Number(resp?.data?.tasks_enqueued || 0);
      appendLog('[simulate] tasks enqueued', { tasks_enqueued: enq });
      message.loading({ content: `已触发${enq}个重算任务，正在生成…`, key: 'sim_recalc', duration: 1 });
      // 轮询 StorageDailyCharge 写入情况
      const ok = await pollDailyCharges(simulateDate, projectId || undefined, 12, 1500);
      if (ok) {
        appendLog('[simulate] daily charges ready', { date: simulateDate, project_id: projectId });
        message.success({ content: '当日仓储费已生成', key: 'sim_recalc' });
        // 过账为收费记录
        try {
          const body2: any = { charge_date: simulateDate };
          if (projectId) body2.project_id = projectId;
          appendLog('[book] request', body2);
          const bookRes = await request.post('/api/v1/billing/storage/daily-charges/book/', body2);
          appendLog('[book] response', bookRes?.data || {});
          message.success('已过账到收费记录');
        } catch (e) {
          appendLog('[book] error', e);
          message.warning('过账失败，请检查后端接口与参数');
        }
        // 自动将筛选日期设置为该日，便于直接查看
        setDateRange([simulateDate, simulateDate]);
        setPagination((p) => ({ ...p, current: 1 }));
        fetchData(1, (pagination.pageSize as number) || 20);
      } else {
        appendLog('[simulate] daily charges not ready (timeout)', { date: simulateDate, project_id: projectId });
        message.warning({ content: '生成结果未就绪，请稍后手动刷新或扩大轮询次数', key: 'sim_recalc' });
      }
    } catch (e: any) {
      appendLog('[simulate] error', e);
      message.error(`触发失败：${e?.message || '未知错误'}`);
    } finally {
      setSimulating(false);
    }
  };

  // 批量下载当前筛选结果（CSV）
  const handleDownloadAll = async () => {
    try {
      setDownloading(true);
      const pageSize = 1000;
      let page = 1;
      const rows: BillingRecordRow[] = [];
      const baseParams: any = {
        ordering: '-created_at',
        locale: 'zh-CN',
      };
      if (systemCodeExact) baseParams.system_code = systemCodeExact;
      if (dateRange?.[0]) baseParams.occurred_from = dateRange[0];
      if (dateRange?.[1]) baseParams.occurred_to = dateRange[1];
      if (operationTypeId) baseParams.operation_type = operationTypeId;
      if (operationCategory) baseParams.operation_category = operationCategory;
      if (projectId) baseParams.project_id = projectId;
      if (isAdmin && customerId) baseParams.customer_id = customerId;
      if (sourceCodeKeyword) baseParams.source_code = sourceCodeKeyword;
      if (nameKeyword) baseParams.display_name = nameKeyword;
      if (ruleCodeKeyword) baseParams.rule_system_code = ruleCodeKeyword;

      while (true) {
        const params = { ...baseParams, page, page_size: pageSize };
        // eslint-disable-next-line no-console
        console.debug('[BillingRecords] download page params:', params);
        const resp = await request.get('/api/v1/billing/billing-records/', { params });
        const list = (resp.data?.results || []) as BillingRecordRow[];
        rows.push(...list);
        const count = resp.data?.count || 0;
        if (rows.length >= count || list.length === 0) break;
        page += 1;
      }

      // 按系统编码做前端精确过滤（忽略大小写），确保导出与筛选一致
      const sysLower = (systemCodeExact || '').trim().toLowerCase();
      const finalRows = sysLower
        ? rows.filter(r => String(r.system_code || '').toLowerCase() === sysLower)
        : rows;

      const csvEscape = (val: any) => {
        const s = val == null ? '' : String(val);
        const escaped = s.replace(/"/g, '""');
        return `"${escaped}"`;
      };

      const headers = (
        isAdmin
          ? ['客户', '收费记录系统编码', '项目编码', '操作类型', '收费项目名称', '收费单位', '单价', '计费数量', '金额', '货币', '相关业务单号', '发生日期']
          : ['收费记录系统编码', '项目编码', '操作类型', '收费项目名称', '收费单位', '单价', '计费数量', '金额', '货币', '相关业务单号', '发生日期']
      );
      const lines: string[] = [];
      lines.push(headers.map(csvEscape).join(','));
      finalRows.forEach(r => {
        const base = [
          r.system_code,
          r.project_system_code,
          r.operation_type_name,
          (r as any).display_name || r.operation_type_name,
          (r as any).unit_label || r.billing_unit,
          r.unit_price != null ? Number(r.unit_price).toFixed(2) : '',
          r.quantity != null ? formatDecimalMax6(r.quantity) : '',
          r.amount != null ? Number(r.amount).toFixed(2) : '',
          r.currency,
          r.source_code,
          r.occurred_on || (r as any).event_timestamp || ''
        ];
        const line = isAdmin ? [ (r as any).customer_name || r.customer_id || '', ...base ] : base;
        lines.push(line.map(csvEscape).join(','));
      });

      const csv = lines.join('\r\n');
      const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing_records_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(`已导出 ${finalRows.length} 条收费记录`);
    } catch (e: any) {
      message.error(`导出失败：${e?.message || '未知错误'}`);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.current as number, pagination.pageSize as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, pagination.current, pagination.pageSize, operationTypeId, operationCategory, projectId, customerId, systemCodeExact, sourceCodeKeyword, dateRange, nameKeyword, ruleCodeKeyword, invoiceKeyword]);

  // 加载操作类型选项（以类别常量为准，展示 CATEGORY_CHOICES）
  useEffect(() => {
    // 固定类别枚举，来自后端 OperationType.CATEGORY_CHOICES
    setOpTypeOptions([
      { value: -1, label: '全部操作类型（按类别筛选）' },
    ]);
  }, []);

  // 加载项目选项（按权限过滤）
  useEffect(() => {
    (async () => {
      try {
        setProjectLoading(true);
        const resp = await getProjectList({ page: 1, page_size: 100, search: '' });
        const results = Array.isArray((resp.data as any)?.results) ? (resp.data as any).results : [];
        setProjectOptions(results.map((p: any) => ({ value: Number(p.id), label: `${p.project_code || p.id} - ${p.name || ''}` })));
      } catch {
        setProjectOptions([]);
      } finally {
        setProjectLoading(false);
      }
    })();
  }, []);

  // 加载客户选项（管理员：全量主账号列表）
  useEffect(() => {
    (async () => {
      try {
        const list = await getCustomerList({ onlyMain: true });
        const options = Array.isArray(list)
          ? list.map((c: any) => ({ value: Number(c.id), label: `${c.customer_code || c.id || ''} - ${c.name || c.username || ''}` }))
          : [];
        if (options.length > 0) setCustomerOptions(options);
      } catch {
        // 忽略错误
      }
    })();
  }, []);

  return (
    <div className="p-6">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Space size={6} align="center">
          <Typography.Title level={3} style={{ margin: 0 }}>收费记录</Typography.Title>
          <Tooltip title="本页面仅展示仓库业务相关的收费记录（如仓储费、操作费等）；购买物流运单产生的运费/保证金等交易记录，请前往「资金账户 - 交易记录」查询">
            <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
          </Tooltip>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => fetchData((pagination.current as number) || 1, (pagination.pageSize as number) || 20)}
          >
            刷新
          </Button>
          {isAdmin && (
            <Button type="primary" onClick={openManualCreateModal}>
              手动创建收费记录
            </Button>
          )}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            disabled={exportCount === 0}
            onClick={handleDownloadAll}
          >
            批量下载 ({exportCount} 条)
          </Button>
        </Space>
      </div>
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input
          allowClear
          placeholder="收费记录系统编码"
          style={{ width: 220 }}
          value={systemCodeExact}
          onChange={(e) => { setSystemCodeExact(e.target.value); setPagination((p) => ({ ...p, current: 1 })); }}
        />
        {isAdmin && (
          <Select
            allowClear
            placeholder="客户"
            style={{ width: 240 }}
            value={customerId}
            onChange={(v) => { setCustomerId(v as number | undefined); setPagination((p) => ({ ...p, current: 1 })); }}
            options={customerOptions}
            showSearch
            optionFilterProp="label"
          />
        )}
        <RangePicker
          allowClear
          onChange={(vals) => {
            if (vals && vals.length === 2) {
              setDateRange([vals[0]?.format('YYYY-MM-DD'), vals[1]?.format('YYYY-MM-DD')]);
              setPagination((p) => ({ ...p, current: 1 }));
            } else {
              setDateRange([undefined, undefined]);
              setPagination((p) => ({ ...p, current: 1 }));
            }
          }}
          placeholder={["开始日期", "结束日期"]}
        />
        <Select
          allowClear
          placeholder="操作类别"
          style={{ width: 160 }}
          value={operationCategory}
          onChange={(v) => setOperationCategory(v as string | undefined)}
          options={[
            { value: 'INBOUND', label: '入库' },
            { value: 'OUTBOUND', label: '出库' },
            { value: 'INVENTORY_MANAGEMENT', label: '在库管理' },
            { value: 'VALUE_ADDED', label: '增值服务' },
            { value: 'ADJUSTMENT', label: '调整' },
          ]}
        />
        <Select
          allowClear
          placeholder="项目"
          style={{ width: 260 }}
          value={projectId}
          onChange={(v) => setProjectId(v as number | undefined)}
          options={projectOptions}
          loading={projectLoading}
          showSearch
          optionFilterProp="label"
        />
        <Input
          allowClear
          placeholder="相关业务单号"
          style={{ width: 240 }}
          value={sourceCodeKeyword}
          onChange={(e) => setSourceCodeKeyword(e.target.value)}
        />
        <Input
          allowClear
          placeholder="收费规则系统编码（模糊实时筛选）"
          style={{ width: 260 }}
          value={ruleCodeKeyword}
          onChange={(e) => { setRuleCodeKeyword(e.target.value); setPagination((p) => ({ ...p, current: 1 })); }}
        />
        <Input
          allowClear
          placeholder="收费项目名称（实时过滤展示名称）"
          style={{ width: 260 }}
          value={nameKeyword}
          onChange={(e) => setNameKeyword(e.target.value)}
        />
        <Input
          allowClear
          placeholder="归属账单（系统编码或ID）"
          style={{ width: 240 }}
          value={invoiceKeyword}
          onChange={(e) => setInvoiceKeyword(e.target.value)}
        />
        <Button type="primary" onClick={() => fetchData(1, (pagination.pageSize as number) || 20)}>搜索</Button>
        <Button onClick={() => { setDateRange([undefined, undefined]); setOperationTypeId(undefined); setProjectId(undefined); setCustomerId(undefined); setSystemCodeExact(''); setSourceCodeKeyword(''); setNameKeyword(''); setRuleCodeKeyword(''); setInvoiceKeyword(''); setPagination((p) => ({ ...p, current: 1 })); }}>重置</Button>
      </div>
      {/* 汇总显示 */}
      {stats && (Array.isArray(stats.byCurrency) ? stats.byCurrency.length >= 0 : false) && (
        (() => {
          const arr = stats.byCurrency || [];
          const single = arr.length === 1;
          const amountText = single
            ? `${arr[0].amount.toFixed(2)} ${arr[0].currency}`
            : (arr.length > 0 ? arr.map(it => `${it.currency} ${it.amount.toFixed(2)}`).join(' | ') : '-')
          return (
            <div
              style={{
                marginBottom: 8,
                padding: '10px 12px',
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap'
              }}
            >
              <Typography.Text style={{ fontWeight: 600 }}>
                当前筛选结果 合计条数：{stats.total_records ?? '-'} 条
              </Typography.Text>
              <Typography.Text style={{ fontWeight: 600 }}>
                合计金额：{amountText}
              </Typography.Text>
            </div>
          );
        })()
      )}
      <Table<BillingRecordRow>
        rowKey={(r) => String(r.id)}
        loading={loading}
        columns={columns}
        dataSource={data}
        onRow={(record) => {
          const key = (record.source_code || '').trim();
          const idx = key ? groupIndexBySource.get(key) : undefined;
          const bg0 = '#e6f7ff'; // deeper light blue
          const bg1 = '#fff7e6'; // deeper light warm
          const style = idx === undefined ? {} : { backgroundColor: idx === 0 ? bg0 : bg1 };
          return { style } as React.HTMLAttributes<any>;
        }}
        pagination={{
          ...pagination,
          total,
          showTotal: (t, range) => `共 ${t} 条，当前 ${range[0]}-${range[1]} 条`,
          onChange: (current, pageSize) => setPagination((p) => ({ ...p, current, pageSize })),
          showQuickJumper: { goButton: <Button size="small">跳转</Button> },
        }}
      />

      <Modal
        title="手动创建收费记录"
        open={manualOpen}
        onCancel={() => setManualOpen(false)}
        footer={null}
        width={860}
        destroyOnClose
      >
        <Form form={manualForm} layout="vertical">
          <Space style={{ width: '100%' }} size={12} wrap>
            <Form.Item name="customer_id" label="客户" rules={[{ required: true, message: '请选择客户' }]}>
              <Select
                showSearch
                style={{ width: 260 }}
                options={customerOptions}
                optionFilterProp="label"
                placeholder="选择客户"
                onChange={(value) => {
                  const cid = value ? Number(value) : undefined;
                  manualForm.setFieldsValue({ project_id: undefined });
                  loadManualProjectsByCustomer(cid);
                }}
              />
            </Form.Item>
            <Form.Item name="project_id" label="项目（可选）">
              <Select
                allowClear
                showSearch
                style={{ width: 300 }}
                options={manualProjectOptions}
                loading={manualProjectLoading}
                optionFilterProp="label"
                disabled={!manualCustomerId}
                placeholder={manualCustomerId ? '可不选，创建客户级收费记录' : '请先选择客户'}
              />
            </Form.Item>
            <Form.Item name="occurred_on" label="发生日期" rules={[{ required: true, message: '请选择发生日期' }]}>
              <DatePicker style={{ width: 180 }} />
            </Form.Item>
            <Form.Item
              name="source_code"
              label="相关业务单号（可选）"
              tooltip="填写后作为这条收费记录的“相关业务单号”，列表里可以按它筛选，客户也能看到；不填则使用系统自动生成的编号"
            >
              <Input allowClear maxLength={100} style={{ width: 260 }} placeholder="例如指令编码、入库单号、外部订单号" />
            </Form.Item>
          </Space>
          <Form.Item name="note" label="内部备注" rules={[{ required: true, message: '请填写备注，说明为什么手动创建这条收费记录' }]}>
            <Input.TextArea rows={2} maxLength={500} showCount placeholder="说明为什么手动创建这条收费记录（内部备注，客户看不到）" />
          </Form.Item>
        </Form>
        <div style={{ padding: 12, background: '#fafafa', borderRadius: 6 }}>
          {manualCustomerId ? (
            <ChargeForm
              key={`${manualCustomerId}-${manualProjectId || 'none'}`}
              fetchHint={fetchManualHint}
              rules={manualRules}
              submitText={manualSubmitting ? '创建中…' : '创建收费记录'}
              onSubmit={handleManualCreate}
              onCancel={() => setManualOpen(false)}
            />
          ) : (
            <Typography.Text type="secondary">请先选择客户（可再选择项目），再选择收费规则或填写临时收费。</Typography.Text>
          )}
        </div>
      </Modal>

      <Modal
        title={(
          <div>
            <span>{`收费依据${basisRecord?.system_code ? ` - ${basisRecord.system_code}` : ''}`}</span>
            <div style={{ fontSize: 12, fontWeight: 400, color: '#614700', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '6px 10px', marginTop: 8, lineHeight: 1.6 }}>
              仅限于非标准操作指令生成的、以及管理员手动创建的收费记录显示此弹窗，用于说明收费依据，其他类型收费记录不提供此弹窗功能，请直接参考收费规则说明
            </div>
          </div>
        )}
        open={!!basisRecord}
        onCancel={() => setBasisRecord(null)}
        footer={<Button onClick={() => setBasisRecord(null)}>关闭</Button>}
        width={640}
      >
        {basisRecord ? (
          <Descriptions column={1} size="small" bordered labelStyle={{ width: 120 }}>
            <Descriptions.Item label="收费项目">{basisRecord.display_name || basisRecord.operation_type_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="单价 × 数量">
              {(() => {
                // 单价（含收费单位）× 数量，例如：0.50 USD (每标签) × 10
                const price = basisRecord.unit_price !== undefined && basisRecord.unit_price !== null ? Number(basisRecord.unit_price).toFixed(2) : '-';
                const unit = String(basisRecord.unit_label || basisRecord.billing_unit || '').trim();
                return `${price} ${basisRecord.currency || ''}${unit ? ` (${unit})` : ''} × ${formatDecimalMax6(basisRecord.quantity)}`;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="总价">{basisRecord.amount !== undefined && basisRecord.amount !== null ? `${Number(basisRecord.amount).toFixed(2)} ${basisRecord.currency || ''}` : '-'}</Descriptions.Item>
            <Descriptions.Item label="收费依据">
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{basisRecord.basis_text || '（无）'}</div>
            </Descriptions.Item>
            {(() => {
              const m: any = basisRecord.meta || basisRecord.metadata || {};
              const note = m?.manual_entry ? String(m?.note || m?.remark || '').trim() : '';
              return note ? <Descriptions.Item label="内部备注"><div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{note}</div></Descriptions.Item> : null;
            })()}
            <Descriptions.Item label="相关业务单号">
              {basisRecord.source_code ? <Typography.Text copyable={{ text: basisRecord.source_code, tooltips: ['复制', '已复制'] }}>{basisRecord.source_code}</Typography.Text> : '-'}
            </Descriptions.Item>
            {(() => {
              // 手动创建时系统自动生成的编号（客户的数据里没有这个字段，只有管理员看得到）；没填相关业务单号时它就是相关业务单号，不重复显示
              const m: any = basisRecord.meta || basisRecord.metadata || {};
              const code = m?.manual_entry ? String(m?.manual_code || '').trim() : '';
              return code && code !== basisRecord.source_code
                ? <Descriptions.Item label="手动录入编号"><Typography.Text type="secondary" copyable={{ text: code, tooltips: ['复制', '已复制'] }}>{code}</Typography.Text></Descriptions.Item>
                : null;
            })()}
          </Descriptions>
        ) : null}
      </Modal>

      {/* 仓储费日度明细弹窗 */}
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{detailTitle}</span>
            <Input
              allowClear
              size="small"
              style={{ width: 260, marginLeft: 16, fontWeight: 400 }}
              placeholder="按库存单元系统编码筛选"
              value={detailUnitFilter}
              onChange={(e) => setDetailUnitFilter(e.target.value)}
            />
            <Button
              size="small"
              icon={<DownloadOutlined />}
              className="storage-detail-download"
              // 推到右侧，紧挨着"总金额"的左边
              style={{ marginLeft: 'auto', fontWeight: 400 }}
              loading={detailDownloading}
              disabled={detailLoading || detailVisibleGroups.length === 0}
              onClick={() => { void downloadDetailExcel(); }}
            >
              下载明细
            </Button>
            <span style={{ marginLeft: 12, marginRight: 56, fontWeight: 600, color: '#1677ff' }}>
              {detailAmount != null ? (() => {
                const cur = detailCurrency === 'USD' ? '$' : (detailCurrency || '');
                const prefix = cur ? `${cur} ` : '';
                return `总金额：${prefix}${detailAmount.toFixed(2)}`;
              })() : ''}
            </span>
          </div>
        )}
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetailAmount(undefined); setDetailCurrency(undefined); }}
        footer={null}
        width={1280}
      >
        <div style={{ fontSize: 12, color: '#614700', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '6px 10px', marginBottom: 10, lineHeight: 1.7 }}>
          <div>
            同一个库存单元每次入库、每次库存结算转入，都会形成一个独立的入库批次，仓储费按批次分别计算（数量、入库时间不同）。下面按库存单元汇总，点开左侧的箭头可以看到各入库批次的明细；汇总行的金额就是该库存单元当日的仓储费。
          </div>
          {detailAmount != null && detailGroups.length > 0 ? (() => {
            // 明细保留精确金额，入账成收费记录时才按分四舍五入一次：向客户说明明细合计和入账金额的关系（始终按整条记录的全部明细算，不受筛选影响）
            const exactTotal = detailGroups.reduce((acc: number, g: any) => acc + Number(g.amount || 0), 0);
            return (
              <div>
                {`明细精确合计 ${formatExactAmount(exactTotal)}，本条收费记录按分四舍五入入账 ${detailAmount.toFixed(2)}（明细里每一行不单独舍入，所以体积很小的批次金额不会被舍成 0）。`}
              </div>
            );
          })() : null}
        </div>
        <Table
          rowKey={(r: any) => String(r.key || r.id)}
          loading={detailLoading}
          dataSource={detailVisibleGroups}
          locale={{ emptyText: detailUnitFilter.trim() ? '没有匹配的库存单元' : '暂无数据' }}
          expandable={{
            expandedRowKeys: detailExpandedKeys,
            onExpandedRowsChange: (keys) => setDetailExpandedKeys([...keys]),
            indentSize: 16,
          }}
          pagination={{
            ...detailPagination,
            showTotal: (t) => `共 ${t} 个库存单元、${detailBatchCount} 个入库批次`,
            onChange: (current, pageSize) => {
              setDetailPagination((prev) => ({
                ...prev,
                current,
                pageSize,
              }));
            },
            onShowSizeChange: (_current, pageSize) => {
              setDetailPagination((prev) => ({
                ...prev,
                current: 1,
                pageSize,
              }));
            },
          }}
          size="small"
          columns={[
            { title: '库存单元系统编码 / 入库批次', dataIndex: 'unit_id', width: 260, render: (v: any, row: any) => {
              if (row.isGroup) {
                const code = detailUnitCodes[String(row.unit_id)] || '-';
                return (
                  <Space size={6}>
                    <b>{code}</b>
                    <Tag color={row.batches > 1 ? 'blue' : 'default'}>{`共 ${row.batches} 个入库批次`}</Tag>
                  </Space>
                );
              }
              return (
                <Space size={6} wrap>
                  <span>{`批次 ${row.batchNo}`}</span>
                  {row.layer_received_on ? <span style={{ color: '#8c8c8c' }}>{`入库 ${row.layer_received_on}`}</span> : null}
                  {isAdmin && row.layer_id != null ? <span style={{ color: '#bfbfbf', fontSize: 12 }}>{`#${row.layer_id}`}</span> : null}
                  {isAdmin && row.duplicateVersion ? (
                    <Tooltip title={`同一个入库批次当天出现了多行（规则版本 ${row.rule_version}），可能是规则版本变更后没有清理旧明细，请检查`}>
                      <Tag color="red">规则版本重复</Tag>
                    </Tooltip>
                  ) : null}
                </Space>
              );
            } },
            { title: '仓库', dataIndex: 'warehouse_id', width: 120, render: (v: any, row: any) => {
              if (!row.isGroup) return null;
              const name = detailWarehouseNames[String(row.warehouse_id)] || row.warehouse_id || '-';
              return <span>{name}</span>;
            } },
            { title: '项目', dataIndex: 'project_id', width: 200, render: (v: any, row: any) => {
              if (!row.isGroup) return null;
              const info = detailProjectInfo[String(row.project_id)] || {};
              const code = info.code || row.project_id;
              const name = info.name || '';
              return <span>{code}{name ? ` - ${name}` : ''}</span>;
            } },
            { title: (
              <span>
                计费价梯(天)
                <Tooltip title="这是该批次适用的仓储费价格档位（按库龄分档），不是批次本身的入库天数；同一价梯里的不同批次费率相同。">
                  <QuestionCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
                </Tooltip>
              </span>
            ), dataIndex: 'age_bucket', width: 120, render: (v: any) => ageBucketText(v) },
            { title: '数量', dataIndex: 'qty', width: 80 },
            { title: '单位体积(m³)', dataIndex: 'unit_volume_m3', width: 130, render: (v: any, row: any) => (v != null && v !== '' ? Number(v).toFixed(6) : (row.isGroup ? '多种' : '-')) },
            { title: '总计体积(m³)', dataIndex: 'total_volume_m3', width: 130, render: (v: any) => v != null ? Number(v).toFixed(6) : '-' },
            { title: '费率(每m³/日)', dataIndex: 'rate', width: 130, render: (v: any, row: any) => (v != null && v !== '' ? Number(v).toFixed(2) : (row.isGroup ? '多种' : '-')) },
            { title: '金额($)', dataIndex: 'amount', width: 110, render: (v: any, row: any) => (v != null ? (row.isGroup ? <b>{formatExactAmount(v)}</b> : formatExactAmount(v)) : '-') },
          ]}
        />
      </Modal>
    </div>
  );
};

export default BillingRecords;


